import { format } from 'date-fns';

export interface DateRange {
  startTime: string;
  endTime: string;
}

const DAY_MS = 1000 * 60 * 60 * 24;
const WINDOW_DAYS = 7;

// Sensor date ranges are stored as `dd/MM/yyyy` strings.
const parseRangeDate = (value: string): Date => {
  const [day, month, year] = value.split('/').map(Number);
  return new Date(year, month - 1, day);
};

const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const addDays = (date: Date, days: number): Date => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const toRange = (start: Date, end: Date): DateRange => ({
  startTime: format(start, 'dd/MM/yyyy'),
  endTime: format(end, 'dd/MM/yyyy')
});

// Previous 7-day window. The first step from a partial current view snaps to
// the last 7 days (e.g. today = 21 → 14–21); each further step moves the
// window back a week (7–14, then 30–7, …).
export const prevWeekWindow = (range: DateRange): DateRange => {
  const today = atMidnight(new Date());
  const start = atMidnight(parseRangeDate(range.startTime));
  const end = atMidnight(parseRangeDate(range.endTime));

  const spanDays = Math.round((end.getTime() - start.getTime()) / DAY_MS);
  const endsTodayOrLater = end.getTime() >= today.getTime();

  if (endsTodayOrLater && spanDays < WINDOW_DAYS) {
    return toRange(addDays(today, -WINDOW_DAYS), today);
  }

  const newEnd = addDays(end, -WINDOW_DAYS);
  return toRange(addDays(newEnd, -WINDOW_DAYS), newEnd);
};

// Next 7-day window, clamped so it never moves past today (e.g. 7–14 → 14–21).
export const nextWeekWindow = (range: DateRange): DateRange => {
  const today = atMidnight(new Date());
  const end = atMidnight(parseRangeDate(range.endTime));

  let newEnd = addDays(end, WINDOW_DAYS);
  if (newEnd.getTime() > today.getTime()) {
    newEnd = today;
  }

  return toRange(addDays(newEnd, -WINDOW_DAYS), newEnd);
};

// True while the current window ends before today, i.e. there is room to move
// the 7-day window forward.
export const canShiftWeekForward = (range: DateRange): boolean => {
  const today = atMidnight(new Date());
  const end = atMidnight(parseRangeDate(range.endTime));

  return end.getTime() < today.getTime();
};
